import React, { useState } from 'react'
import { Form, Link, usePage } from '@inertiajs/react'
import { Eye, EyeOff } from 'lucide-react'
import { AuthLayout } from '@/layouts/AuthLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'

interface PageProps {
  flash: {
    alert?: string
    notice?: string
  }
  [key: string]: unknown
}

function SignInPage() {
  const { flash } = usePage<PageProps>().props
  const [showPassword, setShowPassword] = useState(false)

  return (
    <Form method="post" action="/users/sign_in">
      {({ errors, processing }: { errors: Record<string, string[]>; processing: boolean }) => (
        <div className="space-y-4">
          {/* Flash alert (e.g. "Invalid email or password" from Devise) */}
          {flash?.alert && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {flash.alert}
            </div>
          )}

          {/* Email field */}
          <div className="space-y-1.5">
            <Label htmlFor="user_email">Email</Label>
            <Input
              id="user_email"
              name="user[email]"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              aria-invalid={!!errors['user[email]']}
            />
            {errors['user[email]'] && (
              <p className="text-sm text-destructive mt-1">{errors['user[email]'][0]}</p>
            )}
          </div>

          {/* Password field */}
          <div className="space-y-1.5">
            <Label htmlFor="user_password">Password</Label>
            <div className="relative">
              <Input
                id="user_password"
                name="user[password]"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                className="pr-10"
                aria-invalid={!!errors['user[password]']}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors['user[password]'] && (
              <p className="text-sm text-destructive mt-1">{errors['user[password]'][0]}</p>
            )}
          </div>

          {/* Remember me */}
          <div className="flex items-center gap-2">
            <Checkbox id="user_remember_me" name="user[remember_me]" value="1" />
            <Label htmlFor="user_remember_me" className="font-normal cursor-pointer">
              Remember me
            </Label>
          </div>

          {/* Submit */}
          <Button type="submit" className="w-full" disabled={processing}>
            {processing ? 'Signing in...' : 'Sign in'}
          </Button>

          {/* Forgot password link */}
          <p className="text-center text-sm">
            <Link
              href="/users/password/new"
              className="text-primary hover:underline"
            >
              Forgot your password?
            </Link>
          </p>
        </div>
      )}
    </Form>
  )
}

SignInPage.layout = (page: React.ReactNode) => (
  <AuthLayout title="Sign in" description="Enter your credentials to access your account">
    {page}
  </AuthLayout>
)

export default SignInPage
